// utils/permissionUtils.js
import React, {
  useState,
  useEffect,
} from "react";
import axiosInstance from './axiosInstance';

/**
 * Permission cache to avoid repeated API calls
 */
let permissionsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch user permissions from the server
 * @returns {Promise<Object>} User permissions object
 */
export const fetchUserPermissions = async () => {
  try {
    // Check cache first
    const now = Date.now();
    if (permissionsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      return permissionsCache;
    }

    const response = await axiosInstance.get('/api/permissions/my-permissions');
    
    if (response.data.success) {
      const data = typeof response.data.data === 'string' 
        ? JSON.parse(response.data.data) 
        : response.data.data;
      
      permissionsCache = data.permissions || {};
      cacheTimestamp = now;
      return permissionsCache;
    }
    
    return {};
  } catch (error) {
    console.error('Error fetching permissions:', error);
    return {};
  }
};

/**
 * Clear the permissions cache (useful after login/logout or role changes)
 */
export const clearPermissionsCache = () => {
  permissionsCache = null;
  cacheTimestamp = null;
};

/**
 * Check if user has a specific permission for a module
 * Supports multiple permissions with pipe separator: 'edit|delete'
 * 
 * @param {string} moduleSlug - The module slug (e.g., 'avatar-settings')
 * @param {string} permissionType - Single permission ('edit') or multiple ('edit|delete')
 * @param {string} operator - 'OR' (default) or 'AND' - how to combine multiple permissions
 * @returns {Promise<boolean>} True if user has permission(s)
 * 
 * @example
 * // Check single permission
 * await hasPermission('avatar-settings', 'edit') // true if user can edit
 * 
 * @example
 * // Check multiple permissions with OR (has ANY of these)
 * await hasPermission('avatar-settings', 'edit|delete') // true if user can edit OR delete
 * 
 * @example
 * // Check multiple permissions with AND (has ALL of these)
 * await hasPermission('avatar-settings', 'edit|delete', 'AND') // true only if user can edit AND delete
 */
export const hasPermission = async (moduleSlug, permissionType, operator = 'OR') => {
  // Check if user is admin (admins always have all permissions)
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      const userRole = user?.active_team?.user_role_in_team;
      if (userRole === 'admin') {
        return true;
      }
    } catch (e) {
      console.error('Error parsing user data:', e);
    }
  }

  const permissions = await fetchUserPermissions();
  
  if (!permissions[moduleSlug]) {
    return false;
  }
  
  // Handle single permission
  if (!permissionType.includes('|')) {
    return permissions[moduleSlug][`can_${permissionType}`] || false;
  }
  
  // Handle multiple permissions with pipe separator
  const permissionTypes = permissionType.split('|').map(p => p.trim());
  const modulePermissions = permissions[moduleSlug];
  
  if (operator.toUpperCase() === 'AND') {
    // User must have ALL permissions
    return permissionTypes.every(perm => modulePermissions[`can_${perm}`] || false);
  } else {
    // User must have ANY permission (OR - default)
    return permissionTypes.some(perm => modulePermissions[`can_${perm}`] || false);
  }
};

/**
 * Check multiple permissions at once
 * @param {Array<{module: string, permission: string, operator?: string}>} checks - Array of permission checks
 * @returns {Promise<Object>} Object with results for each check
 * 
 * @example
 * const results = await checkMultiplePermissions([
 *   { module: 'avatar-settings', permission: 'edit' },
 *   { module: 'language-settings', permission: 'edit|delete', operator: 'OR' }
 * ]);
 * // Returns: { 'avatar-settings_edit': true, 'language-settings_edit|delete': false }
 */
export const checkMultiplePermissions = async (checks) => {
  const permissions = await fetchUserPermissions();
  const results = {};
  
  checks.forEach(({ module, permission, operator = 'OR' }) => {
    const key = `${module}_${permission}`;
    
    if (!permissions[module]) {
      results[key] = false;
      return;
    }
    
    // Handle single permission
    if (!permission.includes('|')) {
      results[key] = permissions[module][`can_${permission}`] || false;
      return;
    }
    
    // Handle multiple permissions
    const permissionTypes = permission.split('|').map(p => p.trim());
    const modulePermissions = permissions[module];
    
    if (operator.toUpperCase() === 'AND') {
      results[key] = permissionTypes.every(perm => modulePermissions[`can_${perm}`] || false);
    } else {
      results[key] = permissionTypes.some(perm => modulePermissions[`can_${perm}`] || false);
    }
  });
  
  return results;
};

/**
 * Get all permissions for a specific module
 * @param {string} moduleSlug - The module slug
 * @returns {Promise<Object>} Object with all permission flags
 */
export const getModulePermissions = async (moduleSlug) => {
  const permissions = await fetchUserPermissions();
  return permissions[moduleSlug] || {
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false
  };
};

/**
 * Check if user can perform any action on a module (has at least one permission)
 * @param {string} moduleSlug - The module slug
 * @returns {Promise<boolean>} True if user has any permission
 */
export const hasAnyPermission = async (moduleSlug) => {
  const permissions = await getModulePermissions(moduleSlug);
  return permissions.can_view || permissions.can_add || 
         permissions.can_edit || permissions.can_delete;
};

/**
 * React Hook for permission checking with pipe support
 * Usage: const { hasPermission, loading } = usePermissions('avatar-settings', 'edit|delete');
 * 
 * @param {string} moduleSlug - The module slug
 * @param {string} permissionType - Single or multiple permissions with pipe separator
 * @param {string} operator - 'OR' (default) or 'AND' for multiple permissions
 * @returns {Object} { hasPermission: boolean, loading: boolean }
 */
export const usePermissions = (moduleSlug, permissionType, operator = 'OR') => {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPermission = async () => {
      setLoading(true);
      const result = await hasPermission(moduleSlug, permissionType, operator);
      setHasAccess(result);
      setLoading(false);
    };

    checkPermission();
  }, [moduleSlug, permissionType, operator]);

  return { hasPermission: hasAccess, loading };
};

/**
 * Higher-order component for permission-based rendering
 * 
 * @param {string} moduleSlug - The module slug
 * @param {string} permissionType - Single or multiple permissions
 * @param {string} operator - 'OR' or 'AND'
 * @returns {Function} HOC that wraps a component
 * 
 * @example
 * const ProtectedButton = withPermission('avatar-settings', 'edit')(Button);
 * 
 * @example
 * const AdminOrEditorButton = withPermission('avatar-settings', 'edit|delete', 'OR')(Button);
 */
export const withPermission = (moduleSlug, permissionType, operator = 'OR') => (Component) => {
  return (props) => {
    const { hasPermission: hasAccess, loading } = usePermissions(moduleSlug, permissionType, operator);
    
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!hasAccess) {
      return null;
    }
    
    return <Component {...props} />;
  };
};

/**
 * Component wrapper for conditional rendering based on permissions
 * 
 * @example
 * <PermissionGate module="avatar-settings" permission="edit">
 *   <EditButton />
 * </PermissionGate>
 * 
 * @example
 * <PermissionGate module="avatar-settings" permission="edit|delete" operator="OR">
 *   <AdminControls />
 * </PermissionGate>
 * 
 * @example
 * <PermissionGate 
 *   module="avatar-settings" 
 *   permission="edit|delete" 
 *   operator="AND"
 *   fallback={<div>No permission</div>}
 * >
 *   <FullAccessControls />
 * </PermissionGate>
 */
export const PermissionGate = ({ 
  module, 
  permission, 
  operator = 'OR',
  fallback = null,
  loadingComponent = null,
  children 
}) => {
  const { hasPermission: hasAccess, loading } = usePermissions(module, permission, operator);
  
  if (loading) {
    return loadingComponent || <div>Loading...</div>;
  }
  
  if (!hasAccess) {
    return fallback;
  }
  
  return <>{children}</>;
};